import React from 'react';
import * as theme from '../../../theme';
import Heading from '../../../components/Heading';

function CostPaymentProcess({ title }) {
  const listStyle = {
    paddingLeft: 20,
    marginTop: 7,
    marginBottom: 20,
  };

  const listItemStyle = {
    marginLeft: 10,
    marginBottom: 8,
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 800, position: 'relative', marginLeft: 'auto', marginRight: 'auto' }}>
      <Heading
        title={title}
        subHeading={true}
        center={true}
      />
      <div style={{ ...theme.SubText, position: 'relative', marginRight: 'auto', marginLeft: 'auto' }}>
        As a TruBalance Bioenergetics practitioner, you are required to maintain a valid credit card on file to facilitate seamless scan scheduling, kit mailing, and payment processing for your clients. Below, we outline the costs, payment process, and how to manage payments within the Practitioner Dashboard.<br /><br />

        <strong>Scan Costs</strong><br />
        Each client scan involves the following fees:<br />
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong>Deposit</strong>: $50 (charged to reserve a scan date)
            <ul style={listStyle}>
              <li style={listItemStyle}>Covers administrative costs, kit preparation, and round-trip mailing.</li>
              <li style={listItemStyle}>Refundable if canceled before the kit is mailed.</li>
              <li style={listItemStyle}>Non-refundable once the kit is mailed but can be applied to a rescheduled scan (see Rescheduling Policy).</li>
            </ul>
          </li>
          <li style={listItemStyle}>
            <strong>Balance</strong>: Varies based on scan type (charged on the scan date)
            <ul style={listStyle}>
              <li style={listItemStyle}>Covers the full scan process.</li>
              <li style={listItemStyle}>Charged automatically after verifying kit receipt and client form completion.</li>
            </ul>
          </li>
        </ul>

        <strong>Cost Structure</strong><br />
        The total cost per scan varies depending on the type. The balance is the total minus the $50 deposit. Refer to the Resources section in the dashboard for the latest details.<br />
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong>Initial Scan</strong>: $250 total ($200 balance)<br />
            First scan for a client.
          </li>
          <li style={listItemStyle}>
            <strong>Follow-up Scan</strong>: $235 total ($185 balance)<br />
            $15 discount, only applied if the client has already been scanned by TruBalance.
          </li>
          <li style={listItemStyle}>
            <strong>Multiple Scans (Same Household)</strong>: $225 total per scan ($175 balance)<br />
            Same for both Initial and Follow-up Scans. $25 discount per scan, clients must live at same mailing address. Applies to multiple people scheduling at the same time. All kit materials sent in one mailer.
          </li>
          <li style={listItemStyle}>
            <strong>Practitioner + Family Scan</strong>: $225 total for first scan ($175 balance), $200 total for additional scans ($150 balance)<br />
            This applies whether Initial or a Follow-up Scan. $25 discount for first person, $50 discount for additional scans when performed at the same time. Must be member of your immediate family (spouse, children, pets). Applies to multiple people scheduling at the same time. All kit materials sent in one mailer.
          </li>
          <li style={listItemStyle}>
            <strong>Additional Imprint Remedy</strong>: $20 extra<br />
            Each scan comes with an included Imprint Remedy. Some scans require an additional Imprint Remedy for a select few Focus Scans to ensure gentle detox (noted under Focus Scans).
          </li>
        </ul>

        <strong>Payment Process</strong><br />
        Payments are securely processed. Here's how it works:<br />
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <strong>Card on File Requirement</strong>:
            <ul style={listStyle}>
              <li style={listItemStyle}>A valid, non-expired credit card must be stored in the Billing section of the app.</li>
              <li style={listItemStyle}>The app verifies card validity before processing payments.</li>
              <li style={listItemStyle}>If the card is invalid, you'll be notified to update it. Failure to update may delay scans.</li>
            </ul>
          </li>
          <li style={listItemStyle}>
            <strong>Initiating a Scan</strong>:
            <ul style={listStyle}>
              <li style={listItemStyle}>Navigate to the client's profile via the Practitioner Dashboard (create the client if needed).</li>
              <li style={listItemStyle}>Create a new scan and choose a scan date.</li>
              <li style={listItemStyle}>Confirm the $50 deposit charge during scan creation.</li>
            </ul>
          </li>
          <li style={listItemStyle}>
            <strong>Balance Payment</strong>:
            <ul style={listStyle}>
              <li style={listItemStyle}>On the scan date, our TruBalance team verifies kit receipt and form completion.</li>
              <li style={listItemStyle}>The balance (based on the scan type) is charged to the card on file.</li>
              <li style={listItemStyle}>If the card fails, the scan is paused, and you'll be notified.</li>
            </ul>
          </li>
          <li style={listItemStyle}>
            <strong>Rescheduling Policy</strong>:
            <ul style={listStyle}>
              <li style={listItemStyle}><strong>Before Kit Mailing</strong>: The $50 deposit is fully refundable.</li>
              <li style={listItemStyle}><strong>After Kit Mailing</strong>:
                <ul style={listStyle}>
                  <li style={listItemStyle}>The $50 deposit is non-refundable but can be applied to a rescheduled scan if rescheduled 48+ hours before the scan date.</li>
                  <li style={listItemStyle}>Within 48 hours, rescheduling is case-by-case, and an additional $50 deposit may be charged.</li>
                </ul>
              </li>
            </ul>
          </li>
          <li style={listItemStyle}>
            <strong>Managing Payments</strong>:
            <ul style={listStyle}>
              <li style={listItemStyle}>Update your card on file in the Billing section of the Practitioner Dashboard.</li>
              <li style={listItemStyle}>View transaction history and download receipts in the Billing section.</li>
              <li style={listItemStyle}>Track scan statuses (e.g., kit receipt, form completion) on the Scan details page.</li>
            </ul>
          </li>
        </ul>

        <strong>Handling Payment Issues</strong><br />
        If you encounter a payment discrepancy or have billing questions, contact our support team via the Practitioner Dashboard or email [hello@trubalancebioenergetics.com]. We'll resolve issues promptly.<br /><br />

        <strong>Why We Require a Card on File</strong><br />
        A card on file ensures:<br />
        <ul style={listStyle}>
          <li style={listItemStyle}>Seamless scan scheduling and processing.</li>
          <li style={listItemStyle}>Timely kit mailing to clients.</li>
          <li style={listItemStyle}>Payment for administrative and operational costs.</li>
        </ul>
        This process is powered by our secure integration with our payment processor.<br /><br />

        <strong>Further Support</strong><br />
        For additional guidance, discuss payment processes during your one-on-one onboarding call or join our weekly support calls to address client-specific billing questions.
      </div>
    </div>
  );
}

export default CostPaymentProcess;